import { calculateLandDamage, getTotalAttack, getTotalGuard } from "../selectors/combat";
import { getAttackKind } from "../selectors/formation";
import type { AttackAction, GameEvent, GameState, PlayerLaneState } from "../types";
import { getOpponentPlayerId } from "../types";
import type { TransitionResult } from "./advance-phase";
import { resolveLaneDefeat } from "./resolve-lane-defeat";

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

/** Resolves one source formation against its validated Direct or Flank target. */
export function applyAttack(
  state: Readonly<GameState>,
  action: AttackAction,
  actionSequence: number,
): TransitionResult {
  const attackingPlayerId = action.playerId;
  const defendingPlayerId = getOpponentPlayerId(attackingPlayerId);
  const sourceLaneId = action.laneId;
  const targetLaneId = action.targetLaneId ?? sourceLaneId;
  const sourceLane = state.lanes[sourceLaneId];
  const targetLane = state.lanes[targetLaneId];
  const attackingSide = sourceLane.players[attackingPlayerId];
  const defendingSide = targetLane.players[defendingPlayerId];
  const commander = attackingSide.commander;
  const targetLand = defendingSide.land;

  if (commander === null || targetLand === null) {
    throw new Error("Validated attack is missing its Commander or target Land.");
  }
  const attackKind = getAttackKind(state, attackingPlayerId, sourceLaneId, targetLaneId);
  if (attackKind === null) throw new Error("Validated attack lost its Direct or Flank target.");
  const versionedTargetFields =
    state.schemaVersion === 4 || state.schemaVersion === 5
      ? { sourceLaneId, targetLaneId, attackKind }
      : {};

  const totalAttack = getTotalAttack(state, attackingPlayerId, sourceLaneId);
  const totalGuard = getTotalGuard(state, defendingPlayerId, targetLaneId);
  const damage = calculateLandDamage(totalAttack, totalGuard);
  const previousControl = targetLand.currentControl;
  const currentControl = Math.max(0, previousControl - damage);
  const laneWasDefeated = currentControl === 0;
  const events: GameEvent[] = [];

  events.push({
    type: "attack-declared",
    eventId: eventId(actionSequence, events.length),
    actionSequence,
    playerId: attackingPlayerId,
    defendingPlayerId,
    laneId: sourceLaneId,
    ...versionedTargetFields,
    commanderCardInstanceId: commander.cardInstanceId,
  });
  events.push({
    type: "combat-calculated",
    eventId: eventId(actionSequence, events.length),
    actionSequence,
    playerId: attackingPlayerId,
    defendingPlayerId,
    laneId: sourceLaneId,
    ...versionedTargetFields,
    totalAttack,
    totalGuard,
    damage,
  });
  events.push({
    type: "land-damaged",
    eventId: eventId(actionSequence, events.length),
    actionSequence,
    playerId: defendingPlayerId,
    laneId: targetLaneId,
    ...(state.schemaVersion === 4 || state.schemaVersion === 5 ? { targetLaneId } : {}),
    damage,
    previousControl,
    currentControl,
  });

  const spentAttackingSide: PlayerLaneState = {
    ...attackingSide,
    commander: { ...commander, readiness: "spent" },
  };
  const damagedDefendingSide: PlayerLaneState = {
    ...defendingSide,
    land: { ...targetLand, currentControl },
  };
  const lanes = { ...state.lanes } as Record<keyof typeof state.lanes, typeof sourceLane>;
  lanes[sourceLaneId] = {
    ...sourceLane,
    players: {
      ...sourceLane.players,
      [attackingPlayerId]: spentAttackingSide,
      ...(sourceLaneId === targetLaneId ? { [defendingPlayerId]: damagedDefendingSide } : {}),
    },
  };
  if (sourceLaneId !== targetLaneId) {
    lanes[targetLaneId] = {
      ...targetLane,
      players: { ...targetLane.players, [defendingPlayerId]: damagedDefendingSide },
    };
  }

  let nextState: GameState = {
    ...state,
    players: {
      ...state.players,
      [attackingPlayerId]: {
        ...state.players[attackingPlayerId],
        actionsUsed: {
          ...state.players[attackingPlayerId].actionsUsed,
          attacksMadeFromLanes: [
            ...state.players[attackingPlayerId].actionsUsed.attacksMadeFromLanes,
            sourceLaneId,
          ],
        },
      },
    },
    lanes,
  };

  if (laneWasDefeated) {
    const resolution = resolveLaneDefeat(nextState, targetLaneId, attackingPlayerId);
    nextState = resolution.state;
    const { exhaustedCardInstanceIds, victoryBanners, matchFinished } = resolution;
    events.push({
      type: "lane-defeated",
      eventId: eventId(actionSequence, events.length),
      actionSequence,
      laneId: targetLaneId,
      winnerPlayerId: attackingPlayerId,
      defeatedPlayerId: defendingPlayerId,
      exhaustedCardInstanceIds,
    });
    events.push({
      type: "victory-banner-awarded",
      eventId: eventId(actionSequence, events.length),
      actionSequence,
      playerId: attackingPlayerId,
      laneId: targetLaneId,
      victoryBanners,
    });
    events.push({
      type: "lane-committed",
      eventId: eventId(actionSequence, events.length),
      actionSequence,
      playerId: attackingPlayerId,
      laneId: targetLaneId,
    });
    if (matchFinished) {
      events.push({
        type: "match-finished",
        eventId: eventId(actionSequence, events.length),
        actionSequence,
        winnerPlayerId: attackingPlayerId,
        reason: "two-victory-banners",
      });
    }
  }

  events.push({
    type: "commander-spent",
    eventId: eventId(actionSequence, events.length),
    actionSequence,
    playerId: attackingPlayerId,
    laneId: sourceLaneId,
    commanderCardInstanceId: commander.cardInstanceId,
  });
  return { state: nextState, events };
}
